import React, { useMemo, useCallback, useEffect, useRef } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import PropTypes from 'prop-types';
import { CONSTANTS, routes } from '../Helpers/Configuration';
import { replaceGenericPlaceholder } from '../Helpers/replacePlaceholders';
import {
    Typography,
    Paper,
    makeStyles,
    TextLink
} from '@ellucian/react-design-system/core';
import {
    spacing40,
    spacing60,
    borderWidthThick,
    borderRadiusXSmall,
    colorBorderAlertError,
    boxShadowError,
    spacing20
} from '@ellucian/react-design-system/core/styles/tokens';
import { useLocation, useHistory } from 'react-router-dom';
import { useSnackbar } from '../SnackbarContext/SnackbarContext';
import { useApi } from '../ApiContext';
import HtmlFormattedMessage from '../HtmlFormattedMessage/HtmlFormattedMessage';

const useStyles = makeStyles(() => ({
    gridSpacing: {
        marginTop: spacing40 + ' !important',
    },
    mainGrid: {
        alignItems: 'center',
    },
    thankYou: {
        marginBottom: spacing60,
    },
    message: {
        marginTop: spacing40,
        marginBottom: spacing60,
    },
    container: {
        margin: '4rem auto 4rem auto',
        maxWidth: '800px',
        padding: spacing60,
    },
    root: {
        padding: `${spacing40} 0`,
        width: '100%',
    },
    captchaContainer: {
        display: 'flex',
        flexDirection: 'row-reverse',
        marginBottom: spacing40,
    },
    notVerified: {
        border: `${borderWidthThick} solid ${colorBorderAlertError}`,
        borderRadius: borderRadiusXSmall,
        boxShadow: boxShadowError,
        padding: spacing20,
        display: 'inline-block'
    },
}));

const RetrievalSubmitted = props => {
    const intl = useIntl();
    const classes = useStyles();
    const snackbar = useSnackbar();
    const customId = 'retrievalSubmitted';
    const history = useHistory();
    const location = useLocation();
    const api = useApi();
    if (!location.state?.inquiry || !location.state?.prospect) {
        history.push(routes.retrieval);
        return null;
    }
    const { prospect = '' } = location?.state || {};
    const { communicationTypes, recaptchaActions } = CONSTANTS;
    const { commType } = props;

    const resubmit = useCallback(async () => {
        try {
            await api.inquiries.retrieve(prospect, '', recaptchaActions.resend);
            snackbar.success(intl.formatMessage({ id: 'applicantResentCommunication' }, {
                who: prospect.email,
            }));
        } catch (e) {
            if (e.response) {
                let msg = '';
                const errorData = e.response.data;
                if (errorData?.msg) {
                    msg = e.response.data.msg;
                } else {
                    msg = <FormattedMessage id="retrievalRecaptchaThresholdError" values={{ score: errorData.score, threshold: errorData.threshold }} />;
                }
                snackbar.error(msg);
            }
        }
    }, [prospect]);

    const messageText = useMemo(() => {
        const contact = commType === communicationTypes.email ? prospect?.email : `+${prospect.phone?.code}${prospect.phone?.number}`;
        const richText = decodeURI(intl.formatMessage({ id: 'applicationRetrievalConfirmation' }));
        return replaceGenericPlaceholder(richText, 'contact', contact);
    }, [commType, prospect]);

    const resendButton = useMemo(() => {
        if (commType === communicationTypes.email) {
            return (
                <TextLink
                    id={`${customId}_ResendLink`}
                    onClick={resubmit}
                >
                    {intl.formatMessage({ id: 'resendEmailLinkTextId' })}
                </TextLink>
            );
        }
        else {
            return (
                <TextLink
                    id={`${customId}_ResendLink`}
                >
                    {intl.formatMessage({ id: 'inquiryConfirmResendTextLink' })}
                </TextLink>
            );
        }
    }, [commType]);

    const mainGridRef = useRef(null);

    useEffect(() => {
        if (mainGridRef.current) {
            const timer = setTimeout(() => {
                mainGridRef.current.setAttribute('tabindex', '-1');
                mainGridRef.current.focus({preventScroll: true});
            }, 100);
        
            return () => clearTimeout(timer);
        }
    }, []);

    return (
        <div className={classes.root}>
            <Paper className={classes.container}>
                <div 
                    tabIndex="-1"
                    ref={mainGridRef}
                    className={classes.mainGrid}
                    id={`${customId}_GridContainer`}
                >
                    <div 
                        aria-live="polite" 
                        aria-atomic="true"
                    >
                        <div id={`${customId}_message`} className={classes.message}>
                            <Typography variant="body1">
                                <HtmlFormattedMessage htmlString={messageText} />
                            </Typography>
                            <Typography>
                                {resendButton}
                            </Typography>
                        </div>
                    </div>
                </div>
            </Paper>
        </div>
    );
};

RetrievalSubmitted.propTypes = {
    commType: PropTypes.string.isRequired,
};

export default RetrievalSubmitted;
